import AddIcon from '@mui/icons-material/Add'
import PublicIcon from '@mui/icons-material/Public'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { Stack, Switch, Tooltip } from '@mui/material'
import type { GridColDef, GridRenderCellParams, GridRowParams, GridSortModel } from '@mui/x-data-grid'
import type { ChangeEvent } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import ChipList from '@/components/dataDisplay/ChipList'
import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import FlagIcon from '@/components/dataDisplay/FlagIcon'
import CustomButton from '@/components/inputs/CustomButton'
import PageHeader from '@/components/layouts/PageHeader'
import { USER_ROLES } from '@/constants/userRoles'
import { useDataGridSyncUrlManager } from '@/contexts/DataGridSyncUrlManagerContext'
import { useAuth } from '@/features/authentication/contexts/AuthContext'
import { SiteDisableConformationDialog } from '@/features/site/components/SiteDisableConformationDialog'
import SitesDataGridToolbar from '@/features/site/components/SitesDataGridToolbar'
import { useSitesQuery } from '@/features/site/hooks/useSitesQuery'
import { useUpdateSiteStatusMutation } from '@/features/site/hooks/useUpdateSiteStatusMutation'
import type { SiteListResponse } from '@/features/site/types/site'
import { SiteStatus } from '@/features/site/types/site'
import type { MarketProgramType } from '@/types/marketProgramType'

const DEFAULT_DATA_GRID_SITES_SORT_MODEL: GridSortModel = [{ field: 'name', sort: 'asc' }]

function Sites() {
  const location = useLocation()
  const { loggedInUser } = useAuth()
  const syncUrlManager = useDataGridSyncUrlManager()
  // Disable site confirmation dialog
  const [openDisableSiteConfirmationDialog, setOpenDisableSiteConfirmationDialog] = useState(false)
  const [siteToDisable, setSiteToDisable] = useState<SiteListResponse | null>(null)
  const [pendingSiteStatus, setPendingSiteStatus] = useState<boolean | null>(null)
  const { t } = useTranslation()
  const { updateSiteStatus } = useUpdateSiteStatusMutation()
  const [showDisabledSites, setShowDisabledSites] = useState(false)
  const { sites, isFetching } = useSitesQuery()

  // This location state property is coming from the navigation after creating a new site. You can see the navigate implementation in the SiteCreate.tsx file.
  const newSiteUuid = location.state ? location.state.siteUuid : null

  function getCountryOptions(): CountryIdentifier[] {
    if (!sites) return []

    return Array.from(
      new Set(sites.filter((c) => !!c.countryCode).map((c) => c.countryCode!.toLowerCase() as CountryIdentifier)),
    )
  }

  function hasReadWriteAccessRights() {
    return loggedInUser?.role === USER_ROLES.ADMINISTRATORS.value || loggedInUser?.role === USER_ROLES.TRADERS.value
  }

  function getMarketPrograms(): MarketProgramType[] {
    const marketPrograms = new Set<MarketProgramType>()

    if (sites) {
      for (const site of sites) {
        if (site.marketPrograms) {
          for (const program of site.marketPrograms) {
            if (program?.type) {
              marketPrograms.add(program.type)
            }
          }
        }
      }
    }

    return Array.from(marketPrograms)
  }

  const handleSwitchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, site: SiteListResponse) => {
      const isChecked = event.target.checked
      if (!isChecked) {
        setSiteToDisable(site)
        setPendingSiteStatus(isChecked)
        setOpenDisableSiteConfirmationDialog(true)
      } else {
        updateSiteStatus({
          isActive: isChecked,
          uuid: site.uuid ?? '',
          location: site.location ?? '',
          name: site.name ?? '',
        }).then(() => {
          site.status = isChecked ? SiteStatus.ENABLED : SiteStatus.DISABLED
        })
      }
    },
    [updateSiteStatus],
  )

  const handleConfirmDisable = useCallback(() => {
    if (siteToDisable && pendingSiteStatus !== null) {
      updateSiteStatus({
        isActive: pendingSiteStatus,
        uuid: siteToDisable.uuid ?? '',
        location: siteToDisable.location ?? '',
        name: siteToDisable.name ?? '',
      }).then(() => {
        siteToDisable.status = pendingSiteStatus ? SiteStatus.ENABLED : SiteStatus.DISABLED
        setOpenDisableSiteConfirmationDialog(false)
        setSiteToDisable(null)
        setPendingSiteStatus(null)
      })
    }
  }, [siteToDisable, pendingSiteStatus, updateSiteStatus])

  const handleCancelDisable = useCallback(() => {
    setOpenDisableSiteConfirmationDialog(false)
    setSiteToDisable(null)
    setPendingSiteStatus(null)
  }, [])

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: t('common.enabled'),
      flex: 0.5,
      renderCell: (params: GridRenderCellParams<SiteListResponse>) => (
        <Switch
          checked={params.row.status === SiteStatus.ENABLED}
          color="primary"
          disabled={!hasReadWriteAccessRights()}
          onChange={(event) => handleSwitchChange(event, params.row)}
          onClick={(event) => {
            event.stopPropagation()
          }}
        />
      ),
    },
    {
      field: 'name',
      headerName: t('common.name'),
      flex: 1,
    },
    {
      field: 'address',
      headerName: t('common.address'),
      flex: 2,
    },
    {
      field: 'partner',
      headerName: t('common.partner'),
      flex: 1,
      valueGetter: (_, site: SiteListResponse) => site.partner?.name,
    },
    {
      field: 'country',
      headerName: t('common.country.label'),
      flex: 0.5,
      type: 'singleSelect',
      valueOptions: getCountryOptions(),
      getOptionLabel: (value) => (value ? t(`common.country.${value as CountryIdentifier}`) : '-'),
      valueGetter: (_, site: SiteListResponse) => site.countryCode?.toLowerCase(),
      renderHeader: () => (
        <Tooltip title={t('common.country.label')}>
          <PublicIcon />
        </Tooltip>
      ),
      renderCell: (params) => (
        <Stack sx={{ flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <FlagIcon countryCode={params.row.countryCode?.toLowerCase()} />
        </Stack>
      ),
    },
    {
      field: 'marketPrograms',
      headerName: t('customer.market_programs'),
      flex: 2,
      sortable: false,
      valueGetter: (_, site: SiteListResponse) => site.marketPrograms?.map((program) => program?.type),
      renderCell: (params: GridRenderCellParams<SiteListResponse>) => (
        <ChipList
          chips={
            params.row.marketPrograms?.map((program) => ({
              id: program?.id?.toString() ?? '',
              label: program?.type ? t(`common.market_program.${program?.type}`) : '-',
              sx: { cursor: 'inherit' },
            })) ?? []
          }
          limit={2}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        isRoot
        appBarContent={
          <Stack display="flex" flexDirection="row" gap={2} justifyContent="flex-end" sx={{ width: '100%' }}>
            {hasReadWriteAccessRights() && (
              <>
                <Tooltip title={t('multi_site_bulk_import.button_tooltip')}>
                  <span>
                    <CustomButton
                      color="inheritText"
                      component={Link}
                      startIcon={<UploadFileOutlinedIcon />}
                      to="/sites/multi-site-bulk-import-restrictions"
                      variant="contained"
                    >
                      {t('multi_site_bulk_import.button_label')}
                    </CustomButton>
                  </span>
                </Tooltip>
                <CustomButton
                  color="inheritText"
                  component={Link}
                  startIcon={<AddIcon />}
                  to="/sites/create"
                  variant="contained"
                >
                  {t(`sites.add_new.title`)}
                </CustomButton>
              </>
            )}
          </Stack>
        }
        pageTag={'sites'}
        pageTitle={t('component.page_header.customers')}
      />
      <CustomDataGrid
        clickableRows={{
          navigateTo: (params: GridRowParams<SiteListResponse>) => `/sites/${params.row.uuid}`,
        }}
        columnVisibilityModel={{
          country: hasReadWriteAccessRights() || false,
        }}
        columns={columns}
        getFocusedRow={(rowId) => rowId === newSiteUuid}
        getRowId={(row) => row.uuid}
        initialState={{
          sorting: {
            sortModel: syncUrlManager.initialState.sortModel ?? DEFAULT_DATA_GRID_SITES_SORT_MODEL,
          },
        }}
        isLoading={isFetching}
        rows={(showDisabledSites ? sites : sites?.filter((site) => site.status === SiteStatus.ENABLED)) ?? []}
        slotProps={{
          toolbar: {
            countryOptions: getCountryOptions(),
            showCountryFilter: hasReadWriteAccessRights() || false,
            showDisabledSites: showDisabledSites,
            marketProgramTypes: getMarketPrograms(),
            onToggleDisabledSitesClick: setShowDisabledSites,
          },
        }}
        slots={{
          toolbar: SitesDataGridToolbar,
        }}
      />
      {siteToDisable && (
        <SiteDisableConformationDialog
          customerName={siteToDisable.name}
          open={openDisableSiteConfirmationDialog}
          onCancel={handleCancelDisable}
          onConfirm={handleConfirmDisable}
        />
      )}
    </>
  )
}

export default Sites
